/**
 * Cache leve para requisições de dados quase-estáticos (tabelas de domínio:
 * tipos de atendimento/contrato, estados, etc.).
 *
 * Resolve dois problemas:
 *  - Dedupe de requisição em voo: chamadas concorrentes com a mesma chave
 *    compartilham a mesma Promise (vários `CustomSelect`/fetchers abrindo o mesmo
 *    modal não disparam N requisições idênticas).
 *  - Cache com TTL curto: reaberturas dentro da janela reutilizam o resultado.
 *
 * Falhas NÃO são cacheadas — a entrada é removida para permitir nova tentativa.
 * Use apenas para dados de leitura, estáveis durante a sessão. Para invalidar
 * após mutação desses domínios, chame `invalidateRequestCache(chave)`.
 */

type CacheEntry<T> = {
  value: Promise<T>
  timestamp: number
}

const DEFAULT_TTL_MS = 60_000

const store = new Map<string, CacheEntry<unknown>>()

export const cachedRequest = <T>(
  key: string,
  factory: () => Promise<T>,
  ttlMs: number = DEFAULT_TTL_MS
): Promise<T> => {
  const now = Date.now()
  const cached = store.get(key) as CacheEntry<T> | undefined

  if (cached && now - cached.timestamp < ttlMs) {
    return cached.value
  }

  const value = factory().catch((error) => {
    // Não persiste falha: remove a entrada (se ainda for esta) para liberar retry.
    if (store.get(key)?.value === value) {
      store.delete(key)
    }
    throw error
  })

  store.set(key, { value, timestamp: now })
  return value
}

export const invalidateRequestCache = (key?: string): void => {
  if (key) {
    store.delete(key)
  } else {
    store.clear()
  }
}
